import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowRightIcon,
  ErrorText,
  Eyebrow,
  HeadingItalic,
  HelperText,
  Stamp,
  Wordmark,
} from "../components/design";
import CallerIDNotice from "../components/design/CallerIDNotice";
import VoiceSheet from "../components/onboarding/VoiceSheet";
import { checkCode, registerOrder } from "../lib/api";
import { InlineSpinner } from "../components/Loading";
import { VITANARA_PHONE_DISPLAY } from "../lib/contact";
import { BETA_MODE, isTierAvailable, type Tier } from "../lib/beta";

// ─── Types & State ────────────────────────────────────────────────────────

type Giftee = "grandmother" | "grandfather" | "mother" | "father" | "other";

// Gendered pronoun & noun helpers — the buyer picks the giftee on step 1
// and every subsequent step adapts its copy accordingly. "Andere" stays
// feminine by default; if needed, we can ask for the gender explicitly
// later.
type PersonRefs = {
  Sie: string;      // sentence-initial subject pronoun
  sie: string;      // mid-sentence subject pronoun
  ihre: string;     // possessive: her/his
  Ihre: string;     // possessive capitalised
  sieAcc: string;   // accusative: her/him
  ihr: string;      // dative: her/him
  noun: string;     // Oma / Opa / Mama / Papa / die Person
  Nounified: string; // capitalised version for "Wann passt es <X> am besten?"
  nominal: string;  // "die Erzählerin" / "der Erzähler"
  ueber: string;    // "über sie" / "über ihn" (used in step-4 eyebrow)
};

function getPersonRefs(g: Giftee | null): PersonRefs {
  const isMale = g === "grandfather" || g === "father";
  const nounMap: Record<Exclude<Giftee, null> | "default", string> = {
    grandmother: "Oma",
    grandfather: "Opa",
    mother: "Mama",
    father: "Papa",
    other: "die Person",
    default: "Oma",
  };
  const noun = nounMap[g || "default"];
  if (isMale) {
    return {
      Sie: "Er",
      sie: "er",
      ihre: "seine",
      Ihre: "Seine",
      sieAcc: "ihn",
      ihr: "ihm",
      noun,
      Nounified: noun,
      nominal: "der Erzähler",
      ueber: "über ihn",
    };
  }
  return {
    Sie: "Sie",
    sie: "sie",
    ihre: "ihre",
    Ihre: "Ihre",
    sieAcc: "sie",
    ihr: "ihr",
    noun,
    Nounified: noun,
    nominal: "die Erzählerin",
    ueber: "über sie",
  };
}
type Biographer = "konrad" | "ida";
type PaymentMethod = "card" | "sepa" | "paypal" | "apple";
type CallSlot = "mo-am" | "mi-pm" | "do-am" | "sa-am";

type OnboardingState = {
  // 1
  giftee: Giftee | null;
  // 2
  packageTier: Tier;
  code: string;
  codeDiscountPercent: number;
  // 3
  giftDate: string; // ISO yyyy-mm-dd
  giftOccasion: string;
  // 4
  gifteeName: string;
  gifteeLastName: string;
  gifteeBirthdate: string;
  gifteeTopics: string;
  // 5
  phoneNumber: string;
  callSlot: CallSlot | "";
  // 6
  biographer: Biographer;
  // 7
  ordererEmail: string;
  // 8
  paymentMethod: PaymentMethod;
  cardNumber: string;
  cardExpiry: string;
  cardCvc: string;
  cardHolder: string;
  agbConsent: boolean;
  // Optional warmth fields surfaced on step 4 (extras)
  preferredAddress: string;
  hearingAbility: "" | "gut" | "leicht_eingeschraenkt" | "stark_eingeschraenkt";
  livingSituation: string;
};

const INITIAL: OnboardingState = {
  giftee: null,
  packageTier: "basis",
  code: "",
  codeDiscountPercent: 0,
  giftDate: "",
  giftOccasion: "",
  gifteeName: "",
  gifteeLastName: "",
  gifteeBirthdate: "",
  gifteeTopics: "",
  phoneNumber: "",
  callSlot: "",
  biographer: "konrad",
  ordererEmail: "",
  paymentMethod: "card",
  cardNumber: "",
  cardExpiry: "",
  cardCvc: "",
  cardHolder: "",
  agbConsent: false,
  preferredAddress: "",
  hearingAbility: "",
  livingSituation: "",
};

const STORAGE_KEY = "vitanara.onboarding";
const TOTAL_STEPS = 9;

// Base price per tier in EUR (cents avoided — display only).
const TIER_PRICE: Record<Tier, number> = {
  basis: 149,
  premium: 349,
  exklusiv: 699,
};

function loadState(): OnboardingState {
  if (typeof window === "undefined") return INITIAL;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return INITIAL;
    const parsed = JSON.parse(raw) as Partial<OnboardingState>;
    return { ...INITIAL, ...parsed };
  } catch {
    return INITIAL;
  }
}

function saveState(s: OnboardingState) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* noop */
  }
}

function clearState() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

// ─── Validation ───────────────────────────────────────────────────────────

const phoneRegex = /^[0-9 /().-]{4,}$/;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isStepValid(step: number, s: OnboardingState): boolean {
  switch (step) {
    case 1:
      return !!s.giftee;
    case 2:
      // In Beta the basis tier is preselected and the only valid choice.
      // Code is optional.
      return isTierAvailable(s.packageTier);
    case 3:
      return s.giftDate.trim().length > 0;
    case 4:
      return s.gifteeName.trim().length > 0;
    case 5: {
      const digits = s.phoneNumber.replace(/\D/g, "");
      return (
        phoneRegex.test(s.phoneNumber.trim()) &&
        digits.length >= 6 &&
        !!s.callSlot
      );
    }
    case 6:
      return s.biographer === "konrad" || s.biographer === "ida";
    case 7:
      return emailRegex.test(s.ordererEmail.trim());
    case 8:
      // Beta: kostenlos nur mit gültigem 100-%-Code. Keine Kartenzahlung.
      return s.codeDiscountPercent === 100 && s.agbConsent;
    case 9:
      return true;
    default:
      return false;
  }
}

// Compute the discounted price preview.
function pricePreview(tier: Tier, discountPercent: number): {
  base: number;
  final: number;
} {
  const base = TIER_PRICE[tier];
  const final = Math.max(0, base * (1 - discountPercent / 100));
  return { base, final };
}

function formatEUR(value: number): string {
  return value.toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  });
}

function formatDateDE(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map((x) => Number(x));
  if (!y || !m || !d) return iso;
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.toLocaleDateString("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// ─── Page Shell ───────────────────────────────────────────────────────────

export default function Onboarding() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<OnboardingState>(() => loadState());
  const initialized = useRef(false);

  useEffect(() => {
    saveState(state);
  }, [state]);

  const stepParam = Number(params.get("step") || 1);
  const step = Math.min(
    Math.max(1, Number.isFinite(stepParam) ? stepParam : 1),
    TOTAL_STEPS,
  );

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    if (!params.get("step")) {
      const next = new URLSearchParams(params);
      next.set("step", "1");
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const update = useCallback(
    (patch: Partial<OnboardingState>) =>
      setState((prev) => ({ ...prev, ...patch })),
    [],
  );

  const goToStep = useCallback(
    (n: number) => {
      const next = new URLSearchParams(params);
      next.set("step", String(n));
      setParams(next);
    },
    [params, setParams],
  );

  const goBack = useCallback(() => {
    if (step === 1) navigate("/");
    else goToStep(step - 1);
  }, [step, goToStep, navigate]);

  const goNext = useCallback(() => {
    if (step < TOTAL_STEPS) goToStep(step + 1);
  }, [step, goToStep]);

  const valid = isStepValid(step, state);

  return (
    <div
      className="paper-bg desk-focus-shell"
      style={{
        minHeight: "100vh",
        fontFamily: "var(--f-body)",
        color: "var(--ink)",
      }}
    >
      <div className="corner-mark">
        <Link
          to="/"
          aria-label="Vitanara Startseite"
          style={{ textDecoration: "none" }}
        >
          <Wordmark size={20} />
        </Link>
      </div>
      <div
        style={{
          maxWidth: 460,
          margin: "0 auto",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ChromeBar step={step} onBack={goBack} />
        <div
          style={{
            padding: "32px 22px 30px",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ flex: 1 }}>
            {step === 1 && <Step1 state={state} update={update} />}
            {step === 2 && <Step2 state={state} update={update} />}
            {step === 3 && <Step3 state={state} update={update} />}
            {step === 4 && <Step4 state={state} update={update} />}
            {step === 5 && <Step5 state={state} update={update} />}
            {step === 6 && <Step6 state={state} update={update} />}
            {step === 7 && <Step7 state={state} update={update} />}
            {step === 8 && <Step8 state={state} update={update} />}
            {step === 9 && <Step9 state={state} />}
          </div>
          {step !== 9 && (
            <div style={{ paddingTop: 24 }}>
              <StepFooter
                step={step}
                valid={valid}
                state={state}
                onNext={goNext}
                onSubmitted={() => goToStep(9)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Chrome / footer ──────────────────────────────────────────────────────

function ChromeBar({ step, onBack }: { step: number; onBack: () => void }) {
  return (
    <div
      style={{
        padding: "60px 22px 0",
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}
    >
      <button
        type="button"
        onClick={onBack}
        aria-label="Zurück"
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: "var(--paper-50)",
          border: "1px solid rgba(43,26,15,0.18)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          cursor: "pointer",
          padding: 0,
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--ink-soft)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      <div style={{ flex: 1, display: "flex", gap: 3 }}>
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: 3,
              borderRadius: 2,
              background:
                i < step ? "var(--oxblood)" : "rgba(43,26,15,0.12)",
              transition: "background-color 240ms ease-out",
            }}
          />
        ))}
      </div>
      <div
        style={{
          fontFamily: "var(--f-ui)",
          fontSize: 12,
          color: "var(--ink-faint)",
          minWidth: 32,
          textAlign: "right",
        }}
      >
        {step}/{TOTAL_STEPS}
      </div>
    </div>
  );
}

function PrimaryButton({
  children,
  disabled,
  onClick,
  busy,
  type = "button",
}: {
  children: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  busy?: boolean;
  type?: "button" | "submit";
}) {
  return (
    <button
      type={type}
      className="btn-primary"
      disabled={disabled || busy}
      onClick={onClick}
      style={{ width: "100%", boxSizing: "border-box" }}
    >
      {busy ? <InlineSpinner /> : children}
      {!busy && <ArrowRightIcon />}
    </button>
  );
}

function StepFooter({
  step,
  valid,
  state,
  onNext,
  onSubmitted,
}: {
  step: number;
  valid: boolean;
  state: OnboardingState;
  onNext: () => void;
  onSubmitted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    setBusy(true);
    try {
      await registerOrder({
        interviewee: {
          first_name: state.gifteeName.trim(),
          last_name: state.gifteeLastName.trim(),
          phone: `+49 ${state.phoneNumber.trim()}`,
        },
        orderer: {
          first_name: "",
          last_name: "",
          email: state.ordererEmail.trim().toLowerCase(),
        },
        target_group: [],
        person_info: state.gifteeTopics.trim(),
        life_events: "",
        giftee_relation: state.giftee || undefined,
        biographer: state.biographer,
        gift_birthdate: state.gifteeBirthdate.trim() || undefined,
        code: state.code.trim().toUpperCase(),
        call_slot: state.callSlot || undefined,
        package: state.packageTier,
        gift_date: state.giftDate || undefined,
        gift_occasion: state.giftOccasion || undefined,
        payment_method: state.paymentMethod,
        agb_consent: state.agbConsent,
        preferred_address: state.preferredAddress.trim() || undefined,
        hearing_ability: state.hearingAbility || undefined,
        living_situation: state.livingSituation.trim() || undefined,
      });
      onSubmitted();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Etwas ist schiefgelaufen.");
    } finally {
      setBusy(false);
    }
  }

  if (step === 8) {
    return (
      <>
        <PrimaryButton disabled={!valid} busy={busy} onClick={submit}>
          Kostenlos abschließen
        </PrimaryButton>
        <div
          style={{
            textAlign: "center",
            marginTop: 10,
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 12,
            color: "var(--ink-faint)",
          }}
        >
          Friends-&amp;-Family-Beta: kostenlos · keine Zahlung · 14 Tage
          Widerrufsrecht
        </div>
        {error && (
          <p
            role="alert"
            style={{
              marginTop: 10,
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 13,
              color: "var(--oxblood)",
              textAlign: "center",
            }}
          >
            {error}
          </p>
        )}
      </>
    );
  }

  const labelByStep: Record<number, string> = {
    1: "Weiter",
    2: "Basis wählen · 149 €",
    3: "Weiter",
    4: "Weiter",
    5: "Weiter",
    6: state.biographer === "ida" ? "Ida wählen" : "Konrad wählen",
    7: "Weiter zum Abschluss",
  };

  return (
    <PrimaryButton disabled={!valid} onClick={onNext}>
      {labelByStep[step] || "Weiter"}
    </PrimaryButton>
  );
}

// ─── Step 1 — Beschenkte Person ───────────────────────────────────────────

function Step1({
  state,
  update,
}: {
  state: OnboardingState;
  update: (p: Partial<OnboardingState>) => void;
}) {
  const opts: { v: Giftee; i: string; t: string; d: string }[] = [
    { v: "grandmother", i: "👵", t: "Meine Großmutter", d: "Oma erzählt ihre Geschichte" },
    { v: "grandfather", i: "👴", t: "Mein Großvater", d: "Opa erzählt seine Geschichte" },
    { v: "mother", i: "👩", t: "Meine Mutter", d: "Mama erzählt ihre Geschichte" },
    { v: "father", i: "👨", t: "Mein Vater", d: "Papa erzählt seine Geschichte" },
    { v: "other", i: "✍︎", t: "Jemand anderes", d: "Ein/e Tante, Onkel, Freund/in…" },
  ];
  return (
    <>
      <Eyebrow>Schritt 1 · Beschenkte Person</Eyebrow>
      <HeadingItalic>
        Für wen ist das <em>Geschenk</em>?
      </HeadingItalic>
      <HelperText>
        So wissen Konrad und Ida, wie sie sich vorstellen sollen, wenn sie das
        erste Mal anrufen.
      </HelperText>
      <div
        role="radiogroup"
        aria-label="Beschenkte Person"
        style={{ display: "flex", flexDirection: "column", gap: 10 }}
      >
        {opts.map((o) => (
          <RadioCard
            key={o.v}
            name="giftee"
            value={o.v}
            checked={state.giftee === o.v}
            onChange={() => update({ giftee: o.v })}
            icon={<span style={{ fontSize: 20 }}>{o.i}</span>}
            title={o.t}
            desc={o.d}
          />
        ))}
      </div>
    </>
  );
}

function RadioCard({
  name,
  value,
  checked,
  onChange,
  icon,
  title,
  desc,
}: {
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  icon: ReactNode;
  title: string;
  desc: string;
}) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "14px 16px",
        background: "var(--paper-50)",
        border: checked
          ? "1.5px solid var(--oxblood)"
          : "1px solid rgba(43,26,15,0.12)",
        borderRadius: 12,
        boxShadow: checked ? "0 4px 12px rgba(107,32,24,0.10)" : "none",
        cursor: "pointer",
        minHeight: 64,
      }}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="sr-only"
      />
      <div
        aria-hidden
        style={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: "var(--paper-200)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontFamily: "var(--f-display)",
            fontWeight: 500,
            fontSize: 17,
            color: "var(--ink)",
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontFamily: "var(--f-body)",
            fontSize: 13,
            color: "var(--ink-faint)",
            fontStyle: "italic",
          }}
        >
          {desc}
        </div>
      </div>
      <RadioDot checked={checked} />
    </label>
  );
}

function RadioDot({ checked }: { checked: boolean }) {
  return (
    <div
      aria-hidden
      style={{
        width: 20,
        height: 20,
        borderRadius: "50%",
        flexShrink: 0,
        border: checked
          ? "6px solid var(--oxblood)"
          : "1.5px solid rgba(43,26,15,0.25)",
        background: checked ? "var(--paper-50)" : "transparent",
        boxSizing: "border-box",
      }}
    />
  );
}

// ─── Step 2 — Paket + Aktionscode ────────────────────────────────────────

const PACKAGES: {
  key: Tier;
  name: string;
  price: number;
  tagline: string;
  lines: string[];
}[] = [
  {
    key: "basis",
    name: "Basis",
    price: 149,
    tagline: "PDF-Biografie & Podcast",
    lines: ["PDF · 10 Fotos", "3 Std. Interviews"],
  },
  {
    key: "premium",
    name: "Premium",
    price: 349,
    tagline: "PDF lektoriert & Hörbuch",
    lines: ["Hörbuch · 25 Fotos", "6 Std. Interviews · Lektorat"],
  },
  {
    key: "exklusiv",
    name: "Exklusiv",
    price: 699,
    tagline: "Lebenswerk · Premium-PDF",
    lines: [
      "Goldsatz · 50 Fotos · alle Formate",
      "10 Std. Interviews · VIP-Service",
    ],
  },
];

function Step2({
  state,
  update,
}: {
  state: OnboardingState;
  update: (p: Partial<OnboardingState>) => void;
}) {
  const [codeStatus, setCodeStatus] = useState<
    | { kind: "idle" }
    | { kind: "checking" }
    | { kind: "valid"; percent: number }
    | { kind: "invalid"; message: string }
  >(state.codeDiscountPercent > 0
    ? { kind: "valid", percent: state.codeDiscountPercent }
    : { kind: "idle" });

  // Debounced server validation.
  useEffect(() => {
    const code = state.code.trim();
    if (!code) {
      setCodeStatus({ kind: "idle" });
      if (state.codeDiscountPercent !== 0) {
        update({ codeDiscountPercent: 0 });
      }
      return;
    }
    setCodeStatus({ kind: "checking" });
    const t = window.setTimeout(async () => {
      try {
        const result = await checkCode(code);
        if (result.valid) {
          const percent = result.discount_percent || 0;
          setCodeStatus({ kind: "valid", percent });
          update({ codeDiscountPercent: percent });
        } else {
          setCodeStatus({
            kind: "invalid",
            message: result.message || "Dieser Code ist nicht gültig.",
          });
          update({ codeDiscountPercent: 0 });
        }
      } catch (e: unknown) {
        setCodeStatus({
          kind: "invalid",
          message:
            e instanceof Error ? e.message : "Dieser Code ist nicht gültig.",
        });
        update({ codeDiscountPercent: 0 });
      }
    }, 350);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.code]);

  const { final } = pricePreview(
    state.packageTier,
    codeStatus.kind === "valid" ? codeStatus.percent : 0,
  );

  return (
    <>
      <Eyebrow>Schritt 2 · Paket</Eyebrow>
      <HeadingItalic>
        Welches <em>Paket</em>?
      </HeadingItalic>
      <HelperText>
        Im Beta-Test ist aktuell nur{" "}
        <strong style={{ color: "var(--ink)" }}>Basis</strong> buchbar. Upgrade
        auf Premium/Exklusiv folgt ab 1. September 2026 — kostenlos für Beta-Käufer.
      </HelperText>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {PACKAGES.map((p) => {
          const available = isTierAvailable(p.key);
          const selected = state.packageTier === p.key && available;
          return (
            <button
              type="button"
              key={p.key}
              onClick={() => available && update({ packageTier: p.key })}
              disabled={!available}
              aria-disabled={!available}
              style={{
                position: "relative",
                padding: "14px 16px",
                background: "var(--paper-50)",
                border: selected
                  ? "1.5px solid var(--oxblood)"
                  : "1px solid rgba(43,26,15,0.12)",
                borderRadius: 12,
                boxShadow: selected
                  ? "0 6px 14px rgba(107,32,24,0.10)"
                  : "none",
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
                opacity: available ? 1 : 0.55,
                cursor: available ? "pointer" : "not-allowed",
                textAlign: "left",
                font: "inherit",
                color: "inherit",
                width: "100%",
              }}
            >
              {selected && (
                <div
                  style={{ position: "absolute", top: -8, right: 14 }}
                  aria-hidden
                >
                  <Stamp variant="oxblood">Im Beta verfügbar</Stamp>
                </div>
              )}
              {!available && (
                <div
                  style={{ position: "absolute", top: -8, right: 14 }}
                  aria-hidden
                >
                  <Stamp variant="ink" rotation={0}>
                    Bald
                  </Stamp>
                </div>
              )}
              <RadioDot checked={selected} />
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      fontFamily: "var(--f-display)",
                      fontWeight: 500,
                      fontStyle: selected ? "italic" : "normal",
                      fontSize: 19,
                      color: "var(--ink)",
                      lineHeight: 1,
                    }}
                  >
                    {p.name}
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
                    <span
                      style={{
                        fontFamily: "var(--f-display)",
                        fontWeight: 500,
                        fontSize: 20,
                        color: "var(--oxblood)",
                      }}
                    >
                      {p.price}
                    </span>
                    <span
                      style={{
                        fontFamily: "var(--f-display)",
                        fontStyle: "italic",
                        fontSize: 12,
                        color: "var(--ink-soft)",
                      }}
                    >
                      €
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    fontFamily: "var(--f-body)",
                    fontStyle: "italic",
                    fontSize: 13,
                    color: "var(--ink-faint)",
                    marginTop: 2,
                  }}
                >
                  {p.tagline}
                </div>
                <ul
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: "8px 0 0",
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                  }}
                >
                  {p.lines.map((l) => (
                    <li
                      key={l}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 6,
                        fontFamily: "var(--f-body)",
                        fontSize: 13,
                        color: "var(--ink-soft)",
                        lineHeight: 1.35,
                      }}
                    >
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--ochre)"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ marginTop: 4, flexShrink: 0 }}
                        aria-hidden
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span>{l}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </button>
          );
        })}
      </div>

      {/* Promo code */}
      <div
        style={{
          marginTop: 22,
          background: "var(--paper-50)",
          border: "1px dashed rgba(43,26,15,0.25)",
          borderRadius: 12,
          padding: "14px 16px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8,
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--ochre)"
            strokeWidth="1.8"
            aria-hidden
          >
            <path d="M20 12V6a2 2 0 00-2-2H6a2 2 0 00-2 2v6" />
            <path d="M2 12h20v2.5a2.5 2.5 0 01-2.5 2.5h-15A2.5 2.5 0 012 14.5z" />
            <path d="M12 6v11" />
          </svg>
          <label
            htmlFor="ob-code"
            style={{
              fontFamily: "var(--f-ui)",
              fontSize: 12,
              fontWeight: 600,
              color: "var(--ink)",
              textTransform: "uppercase",
              letterSpacing: "0.12em",
            }}
          >
            Aktionscode
          </label>
        </div>
        <input
          id="ob-code"
          type="text"
          value={state.code}
          onChange={(e) => update({ code: e.target.value.toUpperCase() })}
          placeholder="z. B. OMA2026"
          autoComplete="off"
          aria-invalid={codeStatus.kind === "invalid"}
          style={{
            width: "100%",
            boxSizing: "border-box",
            background: "var(--paper-100)",
            border:
              codeStatus.kind === "invalid"
                ? "1.5px solid var(--error)"
                : "1px solid rgba(43,26,15,0.12)",
            borderRadius: 8,
            padding: "12px 14px",
            fontFamily: "var(--f-display)",
            fontSize: 16,
            letterSpacing: "0.10em",
            color: "var(--ink)",
            textTransform: "uppercase",
            outline: "none",
          }}
        />
        {codeStatus.kind === "valid" && (
          <div
            style={{
              marginTop: 8,
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 12.5,
              color: "var(--sage)",
            }}
          >
            {state.code} → −{codeStatus.percent} % = {formatEUR(final)}
          </div>
        )}
        {codeStatus.kind === "invalid" && (
          <ErrorText style={{ marginTop: 8 }}>{codeStatus.message}</ErrorText>
        )}
        {codeStatus.kind === "checking" && (
          <div
            style={{
              marginTop: 8,
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 12.5,
              color: "var(--ink-faint)",
            }}
          >
            Prüfe Code …
          </div>
        )}
      </div>
    </>
  );
}

// ─── Step 3 — Geschenkdatum ──────────────────────────────────────────────

const OCCASIONS: { key: string; label: string }[] = [
  { key: "birthday", label: "Geburtstag" },
  { key: "christmas", label: "Weihnachten" },
  { key: "mothersday", label: "Muttertag" },
  { key: "fathersday", label: "Vatertag" },
  { key: "just", label: "Einfach so" },
];

function Step3({
  state,
  update,
}: {
  state: OnboardingState;
  update: (p: Partial<OnboardingState>) => void;
}) {
  const today = useMemo(() => new Date(), []);
  const [viewYear, setViewYear] = useState<number>(() => today.getFullYear());
  const [viewMonth, setViewMonth] = useState<number>(() => today.getMonth());

  const monthLabel = useMemo(
    () =>
      new Date(viewYear, viewMonth, 1).toLocaleDateString("de-DE", {
        month: "long",
        year: "numeric",
      }),
    [viewYear, viewMonth],
  );

  // Grid offset: how many blank cells before day 1 (Mon=0..Sun=6).
  const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay();
  const offset = (firstDayOfMonth + 6) % 7; // shift Sun→6
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  function pick(dayNum: number) {
    const iso = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
    update({ giftDate: iso });
  }

  return (
    <>
      <Eyebrow>Schritt 3 · Geschenkdatum</Eyebrow>
      <HeadingItalic>
        Wann soll's <em>losgehen</em>?
      </HeadingItalic>
      <HelperText>
        Wir richten den ersten Anruf so ein, dass er gut zu diesem Anlass passt
        — Geburtstag, Weihnachten, oder einfach ein guter Sonntag.
      </HelperText>

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 18,
        }}
      >
        {OCCASIONS.map((c) => {
          const sel = state.giftOccasion === c.key;
          return (
            <button
              type="button"
              key={c.key}
              onClick={() => update({ giftOccasion: sel ? "" : c.key })}
              style={{
                padding: "8px 14px",
                background: sel ? "var(--oxblood)" : "var(--paper-50)",
                color: sel ? "var(--paper-50)" : "var(--ink-soft)",
                border: sel
                  ? "1.5px solid var(--oxblood)"
                  : "1px solid rgba(43,26,15,0.14)",
                borderRadius: 999,
                fontFamily: "var(--f-ui)",
                fontSize: 12.5,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Calendar */}
      <div
        style={{
          background: "var(--paper-50)",
          border: "1px solid rgba(43,26,15,0.12)",
          borderRadius: 14,
          padding: "16px 16px 14px",
          marginBottom: 18,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 10,
          }}
        >
          <button
            type="button"
            onClick={() => {
              if (viewMonth === 0) {
                setViewMonth(11);
                setViewYear((y) => y - 1);
              } else setViewMonth((m) => m - 1);
            }}
            aria-label="Vorheriger Monat"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 6,
              color: "var(--ink-soft)",
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <div
            style={{
              fontFamily: "var(--f-display)",
              fontStyle: "italic",
              fontSize: 16,
              color: "var(--ink)",
              textTransform: "capitalize",
            }}
          >
            {monthLabel}
          </div>
          <button
            type="button"
            onClick={() => {
              if (viewMonth === 11) {
                setViewMonth(0);
                setViewYear((y) => y + 1);
              } else setViewMonth((m) => m + 1);
            }}
            aria-label="Nächster Monat"
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 6,
              color: "var(--ink-soft)",
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 4,
            textAlign: "center",
          }}
        >
          {["M", "D", "M", "D", "F", "S", "S"].map((d, i) => (
            <div
              key={i}
              style={{
                fontFamily: "var(--f-ui)",
                fontSize: 10,
                fontWeight: 600,
                color: "var(--ink-faint)",
                textTransform: "uppercase",
                padding: "4px 0",
              }}
            >
              {d}
            </div>
          ))}
          {Array.from({ length: offset }).map((_, i) => (
            <div key={`blank-${i}`} />
          ))}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const n = i + 1;
            const iso = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(n).padStart(2, "0")}`;
            const isSelected = state.giftDate === iso;
            const isToday =
              today.getFullYear() === viewYear &&
              today.getMonth() === viewMonth &&
              today.getDate() === n;
            const cellDate = new Date(viewYear, viewMonth, n);
            const todayMid = new Date(
              today.getFullYear(),
              today.getMonth(),
              today.getDate(),
            );
            const inPast = cellDate < todayMid;
            return (
              <button
                type="button"
                key={n}
                onClick={() => !inPast && pick(n)}
                disabled={inPast}
                aria-pressed={isSelected}
                style={{
                  aspectRatio: "1 / 1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "var(--f-display)",
                  fontSize: 14,
                  color: isSelected
                    ? "var(--paper-50)"
                    : inPast
                      ? "var(--ink-faint)"
                      : "var(--ink)",
                  background: isSelected ? "var(--oxblood)" : "transparent",
                  border: isToday && !isSelected
                    ? "1px solid rgba(43,26,15,0.30)"
                    : "none",
                  borderRadius: "50%",
                  opacity: inPast ? 0.4 : 1,
                  fontWeight: isSelected ? 600 : 400,
                  cursor: inPast ? "not-allowed" : "pointer",
                  padding: 0,
                }}
              >
                {n}
              </button>
            );
          })}
        </div>
        {state.giftDate && (
          <div
            style={{
              marginTop: 14,
              paddingTop: 12,
              borderTop: "1px dashed rgba(43,26,15,0.14)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <span className="t-caps">Erster Anruf</span>
            <span
              style={{
                fontFamily: "var(--f-display)",
                fontStyle: "italic",
                fontSize: 16,
                color: "var(--oxblood)",
                textTransform: "capitalize",
              }}
            >
              {formatDateDE(state.giftDate)}
            </span>
          </div>
        )}
      </div>

      <div
        style={{
          background: "rgba(168,122,58,0.10)",
          border: "1px solid rgba(168,122,58,0.30)",
          borderRadius: 12,
          padding: "14px 14px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 10,
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--oxblood)"
            strokeWidth="1.8"
            aria-hidden
          >
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="M3 7l9 6 9-6" />
          </svg>
          <div className="t-caps" style={{ color: "var(--oxblood)" }}>
            Wir melden uns kurz vorher
          </div>
        </div>
        <p
          style={{
            fontFamily: "var(--f-body)",
            fontSize: 14,
            lineHeight: 1.5,
            color: "var(--ink-soft)",
            margin: "0 0 10px",
          }}
        >
          Drei Tage vor dem Geschenkdatum schicken wir Ihnen eine kurze
          Rückfrage-E-Mail — so können Sie noch Themen ergänzen oder das Datum
          verschieben, falls etwas dazwischenkommt.
        </p>
        <div
          style={{
            padding: "10px 12px",
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.10)",
            borderRadius: 8,
            fontFamily: "var(--f-body)",
            fontSize: 12.5,
            color: "var(--ink)",
            lineHeight: 1.45,
          }}
        >
          <div
            style={{
              fontFamily: "var(--f-ui)",
              fontSize: 10,
              fontWeight: 700,
              color: "var(--ink-faint)",
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              marginBottom: 4,
            }}
          >
            Vorschau · Betreff
          </div>
          <div
            style={{
              fontFamily: "var(--f-display)",
              fontWeight: 500,
              fontSize: 14,
              color: "var(--ink)",
            }}
          >
            „Bereit für {state.gifteeName || "den"} ersten Anruf?"
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Step 4 — Über sie ───────────────────────────────────────────────────

function Step4({
  state,
  update,
}: {
  state: OnboardingState;
  update: (p: Partial<OnboardingState>) => void;
}) {
  const refs = useMemo(() => getPersonRefs(state.giftee), [state.giftee]);

  return (
    <>
      <Eyebrow>Schritt 4 · {refs.ueber === "über ihn" ? "Über ihn" : "Über sie"}</Eyebrow>
      <HeadingItalic>
        Erzähl uns von <em>{refs.noun}</em>.
      </HeadingItalic>
      <HelperText>
        Drei kleine Dinge — damit das erste Gespräch warm wird.
      </HelperText>

      <Field
        id="ob-firstname"
        label={`Wie heißt ${refs.sieAcc}?`}
        hint={`So spricht Konrad ${refs.sieAcc} an`}
        value={state.gifteeName}
        onChange={(v) => update({ gifteeName: v })}
        placeholder="z. B. Hildegard"
        required
      />
      <Field
        id="ob-lastname"
        label="Nachname (optional)"
        hint="Wir verwenden ihn nur, wenn nötig"
        value={state.gifteeLastName}
        onChange={(v) => update({ gifteeLastName: v })}
        placeholder="z. B. Wagner"
      />
      <Field
        id="ob-birthdate"
        label={`Wann wurde ${refs.sie} geboren?`}
        hint="Helfen mit dem Zeitgefühl"
        value={state.gifteeBirthdate}
        onChange={(v) => update({ gifteeBirthdate: v })}
        placeholder="14. März 1938"
      />
      <Field
        id="ob-topics"
        label={`Worüber spricht ${refs.sie} gerne?`}
        hint="3–4 Stichworte genügen"
        value={state.gifteeTopics}
        onChange={(v) => update({ gifteeTopics: v })}
        placeholder={
          refs.ihre === "seine"
            ? "Seine Lehrzeit in Schwaben, das Café…"
            : "Ihre Mädchenzeit in Schwaben, das Café…"
        }
        textarea
      />

      {/* Optional warmth section — improves the AI's first call quality. */}
      <div
        style={{
          marginTop: 24,
          paddingTop: 18,
          borderTop: "1px dashed rgba(43,26,15,0.18)",
        }}
      >
        <div
          style={{
            fontFamily: "var(--f-ui)",
            fontSize: 11,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.18em",
            color: "var(--ink-faint)",
            marginBottom: 4,
          }}
        >
          Optional · macht das Gespräch wärmer
        </div>
        <p
          style={{
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 13,
            color: "var(--ink-faint)",
            margin: "0 0 14px",
          }}
        >
          Sie können diese Angaben auch später im Bereich „Mein Buch"
          ergänzen.
        </p>
        <Field
          id="ob-pref-address"
          label={`Wie soll Konrad ${refs.sieAcc} ansprechen?`}
          hint={`z. B. „${state.gifteeName || "Hildegard"}", „${refs.noun}", oder „Frau Müller"`}
          value={state.preferredAddress}
          onChange={(v) => update({ preferredAddress: v })}
          placeholder={state.gifteeName || refs.noun}
        />
        <div style={{ marginBottom: 14 }}>
          <label
            className="t-caps"
            style={{ display: "block", marginBottom: 6 }}
          >
            Hörfähigkeit
          </label>
          <div
            role="radiogroup"
            aria-label="Hörfähigkeit"
            style={{ display: "flex", flexDirection: "column", gap: 6 }}
          >
            {(
              [
                ["gut", "Gut — normales Tempo"],
                ["leicht_eingeschraenkt", "Leicht eingeschränkt — langsamer, deutlicher"],
                ["stark_eingeschraenkt", "Stark eingeschränkt — sehr langsam, kurze Sätze"],
              ] as const
            ).map(([value, label]) => (
              <label
                key={value}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  borderRadius: 10,
                  border:
                    state.hearingAbility === value
                      ? "1.5px solid var(--oxblood)"
                      : "1px solid rgba(43,26,15,0.14)",
                  background:
                    state.hearingAbility === value
                      ? "rgba(107,32,24,0.04)"
                      : "var(--paper-50)",
                  cursor: "pointer",
                  fontFamily: "var(--f-body)",
                  fontSize: 14,
                  color: "var(--ink-soft)",
                }}
              >
                <input
                  type="radio"
                  name="hearing"
                  value={value}
                  checked={state.hearingAbility === value}
                  onChange={() => update({ hearingAbility: value })}
                  style={{ accentColor: "var(--oxblood)" }}
                />
                {label}
              </label>
            ))}
          </div>
        </div>
        <Field
          id="ob-living"
          label="Lebenssituation"
          hint="Hilft uns, sensible Themen wie Partner zu erkennen"
          value={state.livingSituation}
          onChange={(v) => update({ livingSituation: v })}
          placeholder="z. B. alleinlebend, im Pflegeheim, mit Partner"
        />
      </div>
    </>
  );
}

function Field({
  id,
  label,
  hint,
  value,
  onChange,
  placeholder,
  textarea,
  required,
  type = "text",
  inputMode,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  textarea?: boolean;
  required?: boolean;
  type?: string;
  inputMode?: "text" | "tel" | "email" | "numeric";
}) {
  const sharedStyle: CSSProperties = {
    width: "100%",
    boxSizing: "border-box",
    background: "var(--paper-50)",
    border: "1px solid rgba(43,26,15,0.14)",
    borderRadius: 10,
    padding: textarea ? "12px 14px" : "14px 14px",
    fontFamily: "var(--f-body)",
    fontSize: 17,
    color: "var(--ink)",
    lineHeight: 1.4,
    minHeight: textarea ? 70 : 44,
    resize: textarea ? "vertical" : undefined,
    outline: "none",
  };
  return (
    <div style={{ marginBottom: 18 }}>
      <label
        htmlFor={id}
        style={{
          display: "block",
          fontFamily: "var(--f-ui)",
          fontSize: 13,
          fontWeight: 600,
          color: "var(--ink)",
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      {textarea ? (
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          style={sharedStyle}
        />
      ) : (
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          inputMode={inputMode}
          style={sharedStyle}
        />
      )}
      {hint && (
        <div
          style={{
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 12,
            color: "var(--ink-faint)",
            marginTop: 5,
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

// ─── Step 5 — Telefonnummer + Call-Slot ──────────────────────────────────

const CALL_SLOTS: { v: CallSlot; label: string }[] = [
  { v: "mo-am", label: "Mo Vormittag" },
  { v: "mi-pm", label: "Mi Nachmittag" },
  { v: "do-am", label: "Do Vormittag" },
  { v: "sa-am", label: "Sa Vormittag" },
];

function Step5({
  state,
  update,
}: {
  state: OnboardingState;
  update: (p: Partial<OnboardingState>) => void;
}) {
  const refs = useMemo(() => getPersonRefs(state.giftee), [state.giftee]);
  const digits = state.phoneNumber.replace(/\D/g, "");
  const showError =
    state.phoneNumber.trim().length > 0 &&
    (!phoneRegex.test(state.phoneNumber.trim()) || digits.length < 6);

  return (
    <>
      <Eyebrow>Schritt 5 · Telefonnummer</Eyebrow>
      <HeadingItalic>
        Von welcher Nummer wird {refs.sie} <em>anrufen</em>?
      </HeadingItalic>
      <CallerIDNotice who={refs.noun} />

      <label
        htmlFor="ob-phone"
        style={{
          background: "var(--paper-50)",
          border: "1px solid rgba(43,26,15,0.14)",
          borderRadius: 14,
          padding: "14px 18px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          cursor: "text",
        }}
      >
        <div
          style={{
            fontFamily: "var(--f-ui)",
            fontSize: 17,
            fontWeight: 600,
            color: "var(--ink-soft)",
            paddingRight: 12,
            borderRight: "1px solid rgba(43,26,15,0.18)",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span aria-hidden>🇩🇪</span>
          <span>+49</span>
        </div>
        <input
          id="ob-phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={state.phoneNumber}
          onChange={(e) => update({ phoneNumber: e.target.value })}
          placeholder="0621 33 24 81"
          style={{
            flex: 1,
            background: "transparent",
            border: "none",
            outline: "none",
            fontFamily: "var(--f-display)",
            fontSize: 22,
            color: "var(--ink)",
            letterSpacing: "0.02em",
            padding: 0,
          }}
        />
      </label>
      {showError && (
        <p
          role="alert"
          style={{
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 13,
            color: "var(--oxblood)",
            margin: "6px 0 0",
          }}
        >
          Bitte gültige Telefonnummer angeben.
        </p>
      )}

      <div style={{ marginTop: 24 }}>
        <Eyebrow>Wann passt es {refs.noun} am besten?</Eyebrow>
        <p
          style={{
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 13,
            color: "var(--ink-faint)",
            margin: "0 0 12px",
          }}
        >
          Nur als Hinweis für die Familie — Konrad und Ida sind unter{" "}
          <strong style={{ color: "var(--ink)", fontStyle: "normal" }}>
            {VITANARA_PHONE_DISPLAY}
          </strong>{" "}
          rund um die Uhr erreichbar.
        </p>
        <div
          role="radiogroup"
          aria-label="Bevorzugte Anrufzeit"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10,
          }}
        >
          {CALL_SLOTS.map((s) => {
            const sel = state.callSlot === s.v;
            return (
              <label
                key={s.v}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "12px 14px",
                  background: "var(--paper-50)",
                  border: sel
                    ? "1.5px solid var(--oxblood)"
                    : "1px solid rgba(43,26,15,0.12)",
                  borderRadius: 12,
                  boxShadow: sel ? "0 4px 12px rgba(107,32,24,0.10)" : "none",
                  cursor: "pointer",
                }}
              >
                <input
                  type="radio"
                  name="call-slot"
                  value={s.v}
                  checked={sel}
                  onChange={() => update({ callSlot: s.v })}
                  className="sr-only"
                />
                <RadioDot checked={sel} />
                <span
                  style={{
                    fontFamily: "var(--f-display)",
                    fontWeight: 500,
                    fontSize: 14,
                    color: "var(--ink)",
                  }}
                >
                  {s.label}
                </span>
              </label>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ─── Step 6 — Biograf wählen ─────────────────────────────────────────────

function Step6({
  state,
  update,
}: {
  state: OnboardingState;
  update: (p: Partial<OnboardingState>) => void;
}) {
  const [voiceOpen, setVoiceOpen] = useState<null | Biographer>(null);
  return (
    <>
      <Eyebrow>Schritt 6 · Biograf*in</Eyebrow>
      <HeadingItalic>
        Wer soll <em>anrufen</em>?
      </HeadingItalic>
      <HelperText>
        Beide hören geduldig zu. Ihre Stimmen sind unterschiedlich — wählen Sie
        nach Gefühl.
      </HelperText>

      <div
        role="radiogroup"
        aria-label="Biograf"
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        <BioCard
          who="konrad"
          name="Konrad"
          desc="Bedächtige Stimme, ruhig. Stellt offene Fragen und lässt viel Raum zum Erzählen."
          selected={state.biographer === "konrad"}
          onSelect={() => update({ biographer: "konrad" })}
          onPlay={() => setVoiceOpen("konrad")}
        />
        <BioCard
          who="ida"
          name="Ida"
          desc="Warm und neugierig. Hakt freundlich nach und entdeckt gerne kleine Details."
          selected={state.biographer === "ida"}
          onSelect={() => update({ biographer: "ida" })}
          onPlay={() => setVoiceOpen("ida")}
        />
      </div>

      {voiceOpen && (
        <VoiceSheet
          who={voiceOpen}
          onSwitch={(w) => setVoiceOpen(w)}
          onClose={() => setVoiceOpen(null)}
          onChoose={(w) => {
            update({ biographer: w });
            setVoiceOpen(null);
          }}
        />
      )}
    </>
  );
}

function BioCard({
  who,
  name,
  desc,
  selected,
  onSelect,
  onPlay,
}: {
  who: Biographer;
  name: string;
  desc: string;
  selected: boolean;
  onSelect: () => void;
  onPlay: () => void;
}) {
  const src = who === "ida" ? "/images/ida.jpg" : "/images/konrad.jpg";
  return (
    <label
      style={{
        display: "flex",
        gap: 14,
        alignItems: "center",
        padding: 14,
        background: "var(--paper-50)",
        border: selected
          ? "1.5px solid var(--oxblood)"
          : "1px solid rgba(43,26,15,0.12)",
        borderRadius: 14,
        boxShadow: selected ? "0 6px 18px rgba(107,32,24,0.12)" : "none",
        cursor: "pointer",
      }}
    >
      <input
        type="radio"
        name="biographer"
        value={who}
        checked={selected}
        onChange={onSelect}
        className="sr-only"
      />
      <div
        className="oval-frame"
        style={{
          width: 78,
          height: 78,
          flexShrink: 0,
          background: "var(--paper-100)",
          overflow: "hidden",
        }}
      >
        <img
          src={src}
          alt={name}
          className="photo-tone"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            marginBottom: 2,
          }}
        >
          <div
            className="t-script"
            style={{ fontSize: 26, color: "var(--oxblood)", lineHeight: 1 }}
          >
            {name}
          </div>
        </div>
        <div
          style={{
            fontFamily: "var(--f-body)",
            fontSize: 13.5,
            lineHeight: 1.4,
            color: "var(--ink-soft)",
            marginBottom: 6,
          }}
        >
          {desc}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onPlay();
          }}
          style={{
            background: "transparent",
            border: "none",
            padding: 0,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontFamily: "var(--f-ui)",
            fontSize: 12.5,
            fontWeight: 600,
            color: "var(--oxblood)",
            cursor: "pointer",
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <polygon points="6 4 20 12 6 20 6 4" />
          </svg>
          Stimme anhören
        </button>
      </div>
      <RadioDot checked={selected} />
    </label>
  );
}

// ─── Step 7 — Bestätigung ────────────────────────────────────────────────

function Step7({
  state,
  update,
}: {
  state: OnboardingState;
  update: (p: Partial<OnboardingState>) => void;
}) {
  const name =
    [state.gifteeName.trim(), state.gifteeLastName.trim()]
      .filter(Boolean)
      .join(" ") || "—";
  const phone = state.phoneNumber.trim()
    ? `+49 ${state.phoneNumber.trim()}`
    : "—";
  const bio = state.biographer === "ida" ? "Ida" : "Konrad";
  const pkgLabel =
    state.packageTier === "basis"
      ? "Basis · 149 €"
      : state.packageTier === "premium"
        ? "Premium · 349 €"
        : "Exklusiv · 699 €";

  return (
    <>
      <Eyebrow>Schritt 7 · Bestätigung</Eyebrow>
      <HeadingItalic>
        Bereit. {bio} wartet am <em>Hörer</em>.
      </HeadingItalic>
      <HelperText>Eine kleine Übersicht — alles richtig?</HelperText>

      <div
        style={{
          background: "var(--paper-50)",
          border: "1px solid rgba(43,26,15,0.12)",
          borderRadius: 14,
          padding: "8px 18px",
          marginBottom: 16,
        }}
      >
        <Row
          k="Für"
          v={name + (state.gifteeBirthdate ? `, *${state.gifteeBirthdate}` : "")}
        />
        <Row k="Ihre Nummer" v={phone} />
        <Row k="Biograf" v={bio} />
        <Row k="Paket" v={pkgLabel} />
        {state.codeDiscountPercent > 0 && (
          <Row k="Rabatt" v={`${state.code} · −${state.codeDiscountPercent} %`} />
        )}
        <Row
          k="Geschenkdatum"
          v={state.giftDate ? formatDateDE(state.giftDate) : "—"}
          last
        />
      </div>

      <Field
        id="ob-email"
        label="Ihre E-Mail"
        hint="Für Bestätigung und Login in Ihren Bereich"
        value={state.ordererEmail}
        onChange={(v) => update({ ordererEmail: v })}
        placeholder="thomas@beispiel.de"
        type="email"
        inputMode="email"
        required
      />
    </>
  );
}

function Row({ k, v, last }: { k: string; v: string; last?: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        padding: "10px 0",
        borderBottom: last ? "none" : "1px solid rgba(43,26,15,0.08)",
        gap: 14,
      }}
    >
      <div
        style={{
          fontFamily: "var(--f-ui)",
          fontSize: 12,
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          color: "var(--ink-faint)",
        }}
      >
        {k}
      </div>
      <div
        style={{
          fontFamily: "var(--f-display)",
          fontSize: 16,
          color: "var(--ink)",
          textAlign: "right",
          maxWidth: "60%",
          wordBreak: "break-word",
        }}
      >
        {v}
      </div>
    </div>
  );
}

// ─── Step 8 — Abschluss (Beta: kostenlos, nur mit Code) ─────────────────
//
// In der Friends-&-Family-Beta gibt es keine Kartenzahlung. Ein gültiger
// Code mit 100 % Rabatt ist Pflicht; die Bestellung wird über den
// bestehenden /register-Endpoint ohne Zahlung abgeschlossen.
//
// TODO(stripe): Wenn echte Zahlung eingeführt wird, hier wieder eine
// Zahlungsart-Auswahl + @stripe/react-stripe-js Elements einsetzen, wired
// an einen `POST /checkout/create-session`-Endpoint.

function PaymentCodeField({
  state,
  update,
}: {
  state: OnboardingState;
  update: (p: Partial<OnboardingState>) => void;
}) {
  const [draft, setDraft] = useState(state.code || "");
  const [status, setStatus] = useState<"idle" | "checking" | "ok" | "error">(
    state.codeDiscountPercent > 0 ? "ok" : "idle",
  );
  const [error, setError] = useState<string | null>(null);

  async function apply() {
    const next = (draft || "").trim().toUpperCase();
    if (!next) return;
    setStatus("checking");
    setError(null);
    try {
      const r = await checkCode(next);
      if (r.valid) {
        update({ code: next, codeDiscountPercent: r.discount_percent ?? 0 });
        setStatus("ok");
      } else {
        update({ codeDiscountPercent: 0 });
        setStatus("error");
        setError(r.message || "Dieser Code ist nicht gültig.");
      }
    } catch (e: any) {
      setStatus("error");
      setError(e?.message || "Konnte den Code nicht prüfen.");
    }
  }

  return (
    <div style={{ marginBottom: 18 }}>
      <Eyebrow>Aktionscode</Eyebrow>
      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 6,
        }}
      >
        <input
          type="text"
          className="input"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value.toUpperCase());
            setStatus("idle");
            setError(null);
          }}
          placeholder="z. B. OMA2026"
          style={{
            flex: 1,
            textTransform: "uppercase",
            borderColor:
              status === "error"
                ? "var(--error)"
                : status === "ok"
                ? "var(--sage)"
                : undefined,
            borderWidth: status === "ok" || status === "error" ? 1.5 : 1,
          }}
        />
        <button
          type="button"
          className="btn-secondary"
          onClick={apply}
          disabled={status === "checking" || !draft.trim()}
          style={{ padding: "10px 16px", minHeight: 0, fontSize: 14 }}
        >
          {status === "checking" ? <InlineSpinner /> : "Anwenden"}
        </button>
      </div>
      {status === "ok" && state.codeDiscountPercent > 0 && (
        <p
          style={{
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 13,
            color: "var(--sage)",
            margin: "6px 0 0",
          }}
        >
          Rabatt von {state.codeDiscountPercent} % wird oben angewendet.
        </p>
      )}
      {status === "error" && error && (
        <p
          role="alert"
          style={{
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 13,
            color: "var(--error)",
            margin: "6px 0 0",
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
}

function Step8({
  state,
  update,
}: {
  state: OnboardingState;
  update: (p: Partial<OnboardingState>) => void;
}) {
  const { base, final } = pricePreview(
    state.packageTier,
    state.codeDiscountPercent,
  );
  const discountAmount = base - final;
  const pkgLabel = `${state.packageTier[0].toUpperCase()}${state.packageTier.slice(1)} · für ${state.gifteeName || "—"}`;

  return (
    <>
      <Eyebrow>Schritt 8 · Abschluss</Eyebrow>
      <h1
        style={{
          fontFamily: "var(--f-display)",
          fontWeight: 400,
          fontSize: 32,
          lineHeight: 1.05,
          letterSpacing: "-0.015em",
          margin: "0 0 18px",
          color: "var(--ink)",
        }}
      >
        Fast geschafft —<br />
        <em>eine letzte Sache</em>.
      </h1>

      {/* Summary */}
      <div
        style={{
          background: "var(--paper-50)",
          border: "1px solid rgba(43,26,15,0.12)",
          borderRadius: 14,
          padding: "14px 16px",
          marginBottom: 18,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 10,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: "var(--f-display)",
                fontWeight: 500,
                fontSize: 16,
                color: "var(--ink)",
              }}
            >
              {pkgLabel}
            </div>
            <div
              style={{
                fontFamily: "var(--f-body)",
                fontStyle: "italic",
                fontSize: 13,
                color: "var(--ink-faint)",
                marginTop: 2,
              }}
            >
              {state.biographer === "ida" ? "Ida" : "Konrad"}
              {state.giftDate && ` · ${formatDateDE(state.giftDate)}`}
            </div>
          </div>
          <div
            style={{
              fontFamily: "var(--f-display)",
              fontWeight: 500,
              fontSize: 18,
              color: "var(--ink)",
            }}
          >
            {formatEUR(base)}
          </div>
        </div>
        {state.codeDiscountPercent > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              marginTop: 8,
              fontFamily: "var(--f-body)",
              fontSize: 13,
              color: "var(--sage)",
            }}
          >
            <span>
              Aktionscode <strong>{state.code}</strong> (−
              {state.codeDiscountPercent} %)
            </span>
            <span>− {formatEUR(discountAmount)}</span>
          </div>
        )}
        <div
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: "1px dashed rgba(43,26,15,0.18)",
            display: "flex",
            justifyContent: "space-between",
            fontFamily: "var(--f-ui)",
            fontSize: 12,
            color: "var(--ink-faint)",
          }}
        >
          <span>inkl. 19 % MwSt.</span>
          <span
            style={{
              fontFamily: "var(--f-display)",
              fontWeight: 500,
              fontSize: 14,
              color: "var(--ink)",
            }}
          >
            Gesamt {formatEUR(final)}
          </span>
        </div>
      </div>

      <PaymentCodeField state={state} update={update} />

      {state.codeDiscountPercent !== 100 && (
        <div
          style={{
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.12)",
            borderRadius: 12,
            padding: "14px 16px",
            marginBottom: 18,
            fontFamily: "var(--f-body)",
            fontSize: 13.5,
            lineHeight: 1.5,
            color: "var(--ink-soft)",
          }}
        >
          <strong style={{ color: "var(--ink)" }}>
            Vitanara ist in der Beta kostenlos.
          </strong>{" "}
          Geben Sie oben Ihren Friends-&amp;-Family-Code ein, um fortzufahren —
          es fallen keine Kosten an, eine Zahlungsart wird nicht benötigt.
        </div>
      )}

      <label
        style={{
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
          marginTop: 4,
          fontFamily: "var(--f-body)",
          fontSize: 13.5,
          color: "var(--ink-soft)",
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={state.agbConsent}
          onChange={(e) => update({ agbConsent: e.target.checked })}
          className="sr-only"
        />
        <div
          aria-hidden
          style={{
            width: 20,
            height: 20,
            borderRadius: 4,
            flexShrink: 0,
            background: state.agbConsent ? "var(--oxblood)" : "transparent",
            border: state.agbConsent
              ? "none"
              : "1.5px solid rgba(43,26,15,0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {state.agbConsent && (
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--paper-50)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </div>
        <span>
          Ich habe die{" "}
          <Link
            to="/agb"
            style={{ color: "var(--ink)", fontWeight: 600 }}
          >
            AGB
          </Link>{" "}
          und{" "}
          <Link
            to="/datenschutz"
            style={{ color: "var(--ink)", fontWeight: 600 }}
          >
            Datenschutzerklärung
          </Link>{" "}
          gelesen.
        </span>
      </label>
    </>
  );
}

// ─── Step 9 — Erfolg + Timeline ─────────────────────────────────────────

function Step9({ state }: { state: OnboardingState }) {
  const giftName = state.gifteeName || "Hildegard";
  const orderEmail = state.ordererEmail || "Ihre E-Mail";
  const giftDateLabel = state.giftDate
    ? formatDateDE(state.giftDate)
    : "in Kürze";

  return (
    <>
      <div style={{ textAlign: "center", padding: "8px 0 0" }}>
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: "50%",
            background: "var(--paper-50)",
            border: "2px solid var(--oxblood)",
            margin: "20px auto 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            boxShadow: "0 10px 28px rgba(107,32,24,0.20)",
          }}
        >
          <svg
            width="44"
            height="44"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--oxblood)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: -8,
              borderRadius: "50%",
              border: "1px dashed rgba(107,32,24,0.35)",
            }}
          />
        </div>
        <Eyebrow style={{ color: "var(--oxblood)" }}>Vielen Dank!</Eyebrow>
        <h1
          style={{
            fontFamily: "var(--f-display)",
            fontWeight: 400,
            fontSize: 32,
            lineHeight: 1.05,
            letterSpacing: "-0.015em",
            margin: "0 0 14px",
            color: "var(--ink)",
          }}
        >
          {giftName}s Buch
          <br />
          <em>beginnt {giftDateLabel}</em>.
        </h1>
        <p
          style={{
            fontFamily: "var(--f-body)",
            fontSize: 16,
            lineHeight: 1.5,
            color: "var(--ink-soft)",
            margin: "0 auto 20px",
            maxWidth: 320,
          }}
        >
          Wir haben eine Bestätigung an{" "}
          <strong style={{ color: "var(--ink)" }}>{orderEmail}</strong>{" "}
          geschickt — und schreiben gerade {giftName}s Karte mit der Hand.
        </p>
      </div>

      <div style={{ padding: "16px 0 8px" }}>
        <Eyebrow>So geht's weiter</Eyebrow>
        <div
          style={{
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.10)",
            borderRadius: 14,
            padding: "6px 18px",
          }}
        >
          {[
            {
              d: "Heute",
              t: "Bestätigung verschickt",
              a: "Ihre Rechnung liegt im Postfach.",
              done: true,
            },
            {
              d: "Diese Woche",
              t: "Karte schreiben",
              a: "Wir schreiben die Begrüßungskarte für die Post.",
              done: false,
            },
            {
              d: "In Kürze",
              t: "Karte kommt an",
              a: "Mit Datum und der Telefonnummer.",
              done: false,
            },
            {
              d: "3 Tage vorher",
              t: "Rückfrage-E-Mail",
              a: "Letzte Chance, Themen zu ergänzen.",
              done: false,
            },
            {
              d: giftDateLabel,
              t: "Erster Anruf",
              a: `${state.biographer === "ida" ? "Ida" : "Konrad"} stellt sich vor.`,
              done: false,
            },
          ].map((it, i, arr) => (
            <div
              key={it.t}
              style={{
                display: "flex",
                gap: 14,
                padding: "14px 0",
                borderBottom:
                  i < arr.length - 1
                    ? "1px dashed rgba(43,26,15,0.12)"
                    : "none",
                position: "relative",
              }}
            >
              <div
                style={{
                  width: 24,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flexShrink: 0,
                }}
                aria-hidden
              >
                <div
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    background: it.done ? "var(--oxblood)" : "var(--paper-200)",
                    border: it.done
                      ? "none"
                      : "1.5px solid rgba(43,26,15,0.20)",
                    marginTop: 4,
                  }}
                />
                {i < arr.length - 1 && (
                  <div
                    style={{
                      flex: 1,
                      width: 1,
                      background: "rgba(43,26,15,0.12)",
                      marginTop: 4,
                    }}
                  />
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div
                  className="t-caps"
                  style={{
                    color: it.done ? "var(--oxblood)" : "var(--ink-faint)",
                    fontSize: 10,
                  }}
                >
                  {it.d}
                </div>
                <div
                  style={{
                    fontFamily: "var(--f-display)",
                    fontWeight: 500,
                    fontSize: 15.5,
                    color: "var(--ink)",
                    marginTop: 2,
                  }}
                >
                  {it.t}
                </div>
                <div
                  style={{
                    fontFamily: "var(--f-body)",
                    fontStyle: "italic",
                    fontSize: 13,
                    color: "var(--ink-soft)",
                    marginTop: 1,
                  }}
                >
                  {it.a}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ paddingTop: 24 }}>
        <Link
          to="/login"
          className="btn-primary"
          style={{ width: "100%", boxSizing: "border-box" }}
          onClick={() => clearState()}
        >
          Loggen Sie sich jederzeit ein
          <ArrowRightIcon />
        </Link>
        <div style={{ marginTop: 14, textAlign: "center" }}>
          <Link
            to="/"
            style={{
              fontFamily: "var(--f-ui)",
              fontSize: 13,
              color: "var(--ink-faint)",
              textDecoration: "underline",
            }}
            onClick={() => clearState()}
          >
            Zurück zur Startseite
          </Link>
        </div>
      </div>
    </>
  );
}

// ─── Legacy OnboardingDone route (kept so /onboarding/done still works) ──

export function OnboardingDone() {
  return (
    <div
      className="paper-bg"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 22,
        fontFamily: "var(--f-body)",
      }}
    >
      <div
        style={{
          maxWidth: 420,
          width: "100%",
          background: "var(--paper-50)",
          border: "1px solid rgba(43,26,15,0.10)",
          borderRadius: 14,
          padding: "32px 24px",
          boxShadow:
            "0 1px 0 rgba(43,26,15,0.04), 0 14px 24px -8px rgba(43,26,15,0.14)",
          textAlign: "center",
        }}
      >
        <div className="t-script" style={{ fontSize: 36 }}>
          Vielen Dank
        </div>
        <h1
          style={{
            fontFamily: "var(--f-display)",
            fontWeight: 400,
            fontSize: 28,
            lineHeight: 1.1,
            margin: "10px 0 14px",
            color: "var(--ink)",
          }}
        >
          Ihr Geschenk ist <em>angemeldet</em>.
        </h1>
        <p
          style={{
            fontFamily: "var(--f-body)",
            fontSize: 16,
            lineHeight: 1.5,
            color: "var(--ink-soft)",
            margin: "0 0 20px",
          }}
        >
          Wir haben Ihre Anmeldung erhalten und schicken Ihnen in Kürze eine
          Bestätigung per E-Mail.
        </p>
        <Link to="/login" className="btn-primary" style={{ width: "100%" }}>
          Loggen Sie sich jederzeit ein
          <ArrowRightIcon />
        </Link>
        <div style={{ marginTop: 14 }}>
          <Link
            to="/"
            style={{
              fontFamily: "var(--f-ui)",
              fontSize: 13,
              color: "var(--ink-faint)",
              textDecoration: "underline",
            }}
          >
            Zurück zur Startseite
          </Link>
        </div>
      </div>
    </div>
  );
}
